import React, { useEffect, useMemo, useState } from "react";
import { VsiBubble } from "../styled/App.styled";
import { Spinner } from "reactstrap";
import Stockchart from "../components/Stockchart";
import { formatStockChartConfig } from "../utils/Helpers";
import { standardCapacityColors } from "../utils/chartUtils";
import { getReport } from "../utils/Verbs";
import { useVsiAppContext } from "../stores/VsiApp";
import { useAuthWithRoute, useDataWithRoute } from "../utils/hooks";

const defaultMetric = {
  value: "shares",
  label: "Shares",
  route: "file_trend_by_share_proc",
  yAxis: "GiB"
};

const { route: metricRoute } = defaultMetric;

export default function FileTrends(args) {
  const { user: { _id: userId }, client: { clientId }, getPeriodId, getAccessTokenSilently } = useVsiAppContext();
  const periodId = getPeriodId("FILES");

  const adminRoute = useMemo(() => `/api/v6/admin/file_shares_list/${userId}/${clientId}/trends`, [userId, clientId]);
  const [savedChangesData, { isAdminLoading }] = useAuthWithRoute(adminRoute);

  const dataRoute = useMemo(() => `/api/v6/report/${metricRoute}?client_id=${clientId}&period_id=${periodId}`, [clientId, periodId]);
  const [{ recordsets }, { isLoading: isDataLoading }] = useDataWithRoute(dataRoute);

  const [chartData, shareData] = recordsets;

  const isLoading = isAdminLoading || isDataLoading;

  const { formattedChartData } = useMemo(() => {
    const remapData = (chartData || []).map(datum => {
      return { value: datum.share_name, label: datum.share_name };
    });

    const chartOptions = savedChangesData?.[0]?.sharesList?.length ? savedChangesData[0].sharesList : [remapData[0], remapData[1], remapData[2], remapData[3], remapData[4]];

    let newData = [];
    for (let i = 0; i < shareData?.length; i++) {
      for (let j = 0; j < chartOptions?.length; j++) {
        if (shareData[i].share_name === chartOptions[j].value) {
          newData.push(shareData[i]);
        }
      }
    }

    if (newData?.[newData.length - 1]?.["used"]) {
      for (let i = 0; i < chartOptions.length; i++) {
        let sortedArr = [];
        for (let j = 0; j < newData.length; j++) {
          if (newData[j].share_name === chartOptions[i].value) {
            sortedArr.push(newData[j]);
          }
        }
        let currentDate = sortedArr[sortedArr.length - 1];
        let finDate = sortedArr[sortedArr.length - 2] || null;
        const growthRate = (finDate?.used - currentDate?.used) / currentDate?.used * 100;

        let forecastArr = [];

        if (growthRate) {
          // loop to create forecast for 6 months
          for (let i = 1; i <= 6; i++) {
            let forecastAmt = currentDate?.used + ((-growthRate / 100) * currentDate?.used * i);
            let forecastDate = new Date(currentDate.collection_date).setMonth(new Date(currentDate.collection_date).getMonth() + i);
            forecastArr.push({
              share_name: currentDate.share_name,
              collection_date: new Date(forecastDate).toISOString(),
              used: forecastAmt
            });
          }

          newData = newData.concat(forecastArr);
        }
      }
    }

    const renamed = newData.map(datum => {
      return {
        name: datum.share_name,
        field: "used",
        dataLabels: { format: "{point.y:,.0f}" },
        tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
      };
    });

    const config = formatStockChartConfig(newData, renamed, "share_name");

    config.series = config.series.filter((value, i, array) =>
        i === array.findIndex((datum) => (
          datum.place === value.place && datum.name === value.name
        ))
    );
    return {
      formattedChartData: config.series
    };
  }, [chartData, shareData, savedChangesData]);

  return isLoading ? (
    <Spinner color={"success"} />
  ) : (
    <VsiBubble>
      <Stockchart
        height={400}
        navDisabled={true}
        series={formattedChartData}
        config={{
          colors: standardCapacityColors,
          yAxis: {
            title: {
              text: "GiB"
            }
          }
        }}
        formatData={"{y:,2f}"}
        formatLabels={"{text}"}
        formatPoint={`{series.name}`}
      />
    </VsiBubble>
  );


}
